import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import { useClientAthlete } from "../../../context/ClientAthleteContext";
import ClientChildSwitcher from "./ClientChildSwitcher";
import { mediaUrl } from "../../../utils/clientHeroTheme";
import useClubPortalBranding from "../../../hooks/useClubPortalBranding";
import { useClientRefresh } from "../../../hooks/useClientRefresh";
import GroupLeaderboardChart from "../common/GroupLeaderboardChart";
import s from "./clientApp.module.css";
import { Avatar, Skeleton } from "../../ui";

const PERIODS = [
  { id: "month", label: "Месяц" },
  { id: "quarter", label: "90 дней" },
];

function rankMedal(rank) {
  if (rank === 1) return "🥇";
  if (rank === 2) return "🥈";
  if (rank === 3) return "🥉";
  return String(rank);
}

function podiumOrder(rows) {
  const top = rows.filter((r) => r.rank <= 3).sort((a, b) => {
    const order = { 2: 0, 1: 1, 3: 2 };
    return order[a.rank] - order[b.rank];
  });
  return top;
}

export default function ClientStats() {
  const { athleteQueryParam, isParentMode, athletes } = useClientAthlete();
  const { shellStyle, theme } = useClubPortalBranding("ca", null, false);
  const [period, setPeriod] = useState("month");
  const [groupId, setGroupId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await clinicAPI.getMyAttendanceStats({
        ...athleteQueryParam,
        period,
        group_id: groupId || undefined,
      });
      setData(res);
      if (!groupId && res.group?.id) {
        setGroupId(res.group.id);
      }
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [athleteQueryParam, period, groupId]);

  useEffect(() => {
    load();
  }, [load]);

  useClientRefresh(load);

  const my = data?.my_stats;
  const rows = data?.leaderboard || [];
  const podium = useMemo(() => podiumOrder(rows), [rows]);
  const groups = data?.groups || [];

  if (loading && !data) {
    return <Skeleton height={320} style={{ borderRadius: 16 }} />;
  }

  const hasGroup = Boolean(data?.group?.id);

  return (
    <div className={s.statsPage} style={shellStyle}>
      <header className={s.statsHead}>
        <Link to="/app/profile/account" className={s.statsBack}>
          ← Настройки
        </Link>
        <h1 className={s.statsTitle}>Рейтинг</h1>
        <p className={s.statsSub}>Соревнуйтесь за лучшую посещаемость</p>
        {isParentMode && athletes.length > 1 ? (
          <ClientChildSwitcher variant="inline" />
        ) : null}
      </header>

      {!hasGroup ? (
        <div className={s.statsEmpty}>
          <span aria-hidden>🏅</span>
          <p>Вы пока не в группе</p>
          <small>Когда тренер добавит вас в группу, здесь появится рейтинг</small>
        </div>
      ) : (
        <>
          <div className={s.statsHero}>
            <div className={s.statsHeroRank}>
              <span className={s.statsHeroMedal}>
                {my?.rank ? rankMedal(my.rank) : "—"}
              </span>
              <div>
                <strong>
                  {my?.rank
                    ? `${my.rank} место`
                    : "Нет в рейтинге"}
                </strong>
                <span>
                  {my?.total_ranked
                    ? `из ${my.total_ranked} спортсменов`
                    : data?.period_label}
                </span>
              </div>
            </div>
            <p className={s.statsMotivation}>{data?.motivation}</p>
          </div>

          <div className={s.statsKpis}>
            <div className={s.statsKpi}>
              <strong>{my?.pct != null ? `${my.pct}%` : "—"}</strong>
              <span>Явка</span>
            </div>
            <div className={s.statsKpi}>
              <strong>{my?.present ?? 0}</strong>
              <span>Посещений</span>
            </div>
            <div className={s.statsKpi}>
              <strong>{my?.streak ?? 0}</strong>
              <span>Серия</span>
            </div>
          </div>

          {groups.length > 1 ? (
            <div className={s.statsGroupPick} role="tablist" aria-label="Группы">
              {groups.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  role="tab"
                  aria-selected={String(groupId) === String(g.id)}
                  className={
                    String(groupId) === String(g.id)
                      ? s.statsGroupChipOn
                      : s.statsGroupChip
                  }
                  onClick={() => setGroupId(g.id)}
                >
                  {g.name}
                </button>
              ))}
            </div>
          ) : data?.group?.name ? (
            <p className={s.statsGroupName}>{data.group.name}</p>
          ) : null}

          <div className={s.statsPeriods} role="tablist" aria-label="Период">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                type="button"
                role="tab"
                aria-selected={period === p.id}
                className={period === p.id ? s.statsPeriodOn : s.statsPeriod}
                onClick={() => setPeriod(p.id)}
              >
                {p.label}
              </button>
            ))}
          </div>

          {loading ? (
            <Skeleton height={200} style={{ borderRadius: 16, margin: "0 16px" }} />
          ) : rows.length === 0 ? (
            <div className={s.statsEmptyInline}>
              <p>За период ещё нет отмеченных занятий</p>
              <small>Ходите на тренировки — и поднимайтесь в таблице</small>
            </div>
          ) : (
            <div className={`statsChartTheme ${s.statsBoard}`}>
              {podium.length > 0 ? (
                <div className={s.statsPodium} aria-label="Топ-3">
                  {podium.map((row) => (
                    <div
                      key={row.patient_id}
                      className={`${s.statsPodiumCol} ${
                        row.rank === 1
                          ? s.statsPodiumFirst
                          : row.rank === 2
                            ? s.statsPodiumSecond
                            : s.statsPodiumThird
                      } ${row.is_me ? s.statsPodiumMe : ""}`}
                    >
                      <Avatar
                        name={row.full_name}
                        src={mediaUrl(row.photo_url)}
                        size="sm"
                      />
                      <span className={s.statsPodiumMedal}>{rankMedal(row.rank)}</span>
                      <strong>{row.full_name.split(" ")[0]}</strong>
                      <span className={s.statsPodiumPct}>
                        {row.pct != null ? `${row.pct}%` : "—"}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}

              <GroupLeaderboardChart
                rows={rows}
                accent={theme.accent}
                maxBars={15}
                title="Сравнение явки"
              />

              <ol className={s.statsList}>
                {rows.map((row) => (
                  <li
                    key={row.patient_id}
                    className={row.is_me ? s.statsRowMe : s.statsRow}
                  >
                    {row.separator ? (
                      <span className={s.statsRowSep}>···</span>
                    ) : null}
                    <span className={s.statsRowRank}>{row.rank}</span>
                    <Avatar
                      name={row.full_name}
                      src={mediaUrl(row.photo_url)}
                      size="sm"
                    />
                    <div className={s.statsRowMain}>
                      <strong>
                        {row.full_name}
                        {row.is_me ? " (вы)" : ""}
                      </strong>
                      <span>
                        {row.present} посещений
                        {row.streak >= 2 ? ` · серия ${row.streak}` : ""}
                      </span>
                    </div>
                    <span className={s.statsRowPct}>
                      {row.pct != null ? `${row.pct}%` : "—"}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </>
      )}
    </div>
  );
}
